import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ServiceCode, ServiceType, ToastType } from '@common/enums';
import { EpisodeContext } from '@frontend/components/pages/episodes/EpisodeContext';
import type { IPatient } from '../../../../common/interfaces/patient';
import type { IReduxState } from '../../../configureStore';
import type { IDiagnosticReportGetOne } from '../../interfaces/diagnosticReportGetOne';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { checkUsesZorgmail } from '../../../utils/checkUsesZorgmail';
import { showServiceToast } from '../../../utils/showToast';
import { SendRequestMessageModal } from '../../pages/episodes/SendRequestMessageModal';
import { EvaluationDiagnosticReport } from '../../pages/evaluation/EvaluationDiagnosticReport';
import { Button } from '../common';

interface IConfirmDiagnosticReportButtonProps {
    acceptDiagnosticReport?: () => Promise<void>;
    diagnosticReport: IDiagnosticReportGetOne;
    patient: IPatient;
    reloadAll?: () => void;
    request: IRequestGetOne;
    requestMessageToGP?: boolean;
    serviceCode: ServiceCode;
}

export function ConfirmDiagnosticReportButton(props: IConfirmDiagnosticReportButtonProps) {
    const { acceptDiagnosticReport, diagnosticReport, patient, reloadAll, request, requestMessageToGP, serviceCode } =
        props;
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [showEvaluation, setShowEvaluation] = useState(false);
    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);

    if (
        !diagnosticReport.authorization ||
        !diagnosticReport.authorization.canUpdateDiagnosticReportToClosed ||
        !acceptDiagnosticReport
    ) {
        return null;
    }

    return (
        <EpisodeContext.Consumer>
            {(context) => (
                <>
                    <Button
                        automation="sendToHis"
                        color="secondary"
                        large
                        onClick={async () => {
                            if (requestMessageToGP) {
                                setIsModalVisible(true);
                            } else {
                                context?.changeShowRecommendations(true);
                                await confirm(false);
                            }
                        }}
                    >
                        {translateKey('diagnosticReport.sendToHis')}
                    </Button>

                    <SendRequestMessageModal
                        hideHeaderCloseButton
                        isMessageRequired={false}
                        isOpen={isModalVisible}
                        messagePlaceholder={translateKey('common.remarksOrCommentsOptional')}
                        onClose={async () => {
                            setIsModalVisible(false);
                        }}
                        onSent={async () => {
                            setIsModalVisible(false);
                            context?.changeShowRecommendations(true);
                            await confirm(true);
                        }}
                        requestId={request.id}
                        sendButtonText={translateKey('diagnosticReport.sendToHis')}
                        subTitle={translateKey('requestMessage.optionallySendMessageToGP')}
                        title={translateKey('requestMessage.closeConsultation')}
                        toPractitioner={request.draftCareProviderPractitioner!}
                    />

                    <EvaluationDiagnosticReport
                        isOpen={showEvaluation}
                        onClose={async () => {
                            setShowEvaluation(false);

                            if (reloadAll && requestMessageToGP) {
                                reloadAll();
                            }

                            await confirmDiagnosticReportToast(serviceCode, request, patient, selectedEmploymentId);
                            await acceptDiagnosticReport!();
                        }}
                        requestId={request.id}
                    />
                </>
            )}
        </EpisodeContext.Consumer>
    );

    async function confirm(reload: boolean) {
        await confirmDiagnosticReport(diagnosticReport.id);

        if (showEvaluationForService()) {
            setShowEvaluation(true);
        } else {
            if (reloadAll && reload) {
                reloadAll();
            }

            await confirmDiagnosticReportToast(serviceCode, request, patient, selectedEmploymentId);
            await acceptDiagnosticReport!();
        }
    }

    function showEvaluationForService() {
        return request.serviceType === ServiceType.consultation && request.service.code !== ServiceCode.OCRK;
    }
}

export async function confirmDiagnosticReport(diagnosticReportId: string, noAutoSubsequentServices = false) {
    const url = `/api/diagnosticReports/${diagnosticReportId}/close?noAutoSubsequentServices=${noAutoSubsequentServices}`;
    await fetchJson(url, 'POST');
}

export async function confirmDiagnosticReportToast(
    serviceCode: ServiceCode,
    request: IRequestGetOne,
    patient: IPatient,
    employmentId: string | undefined
) {
    if (employmentId && (await checkUsesZorgmail(request.serviceId, patient, employmentId))) {
        showServiceToast(
            ToastType.info,
            'toast.requestConfirmedAndSentToHis',
            serviceCode,
            'diagnosticReport',
            translateKey('toast.confirmedRequestDefaultMessageAndSentToHis')
        );
    } else {
        showServiceToast(
            ToastType.info,
            'toast.requestConfirmed',
            serviceCode,
            'diagnosticReport',
            translateKey('toast.confirmedRequestDefaultMessage')
        );
    }
}
